import "./i18n";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import api, { BACKEND_URL } from "@/lib/api";
import Dashboard from "@/pages/Dashboard";
import Businesses from "@/pages/Businesses";
import Agents from "@/pages/Agents";
import AgentEditor from "@/pages/AgentEditor";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Workflows from "@/pages/Workflows";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import Conversations from "@/pages/Conversations";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Users from "@/pages/Users";
import Embed from "@/pages/Embed";
import WhatsApp from "@/pages/WhatsApp";
import WhatsAppCommands from "@/pages/WhatsAppCommands";
import WhatsAppDocs from "@/pages/WhatsAppDocs";
import SaaSAdmin from "@/pages/SaaSAdmin";
import AgencyPanel from "@/pages/AgencyPanel";

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin-only route wrapper: non-admin users are redirected to Dashboard
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout with sidebar for authenticated routes
const AuthenticatedLayout = ({ children }) => (
  <div className="flex h-screen bg-background overflow-hidden">
    <Sidebar />
    <main className="flex-1 overflow-auto">{children}</main>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Dashboard />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AuthenticatedLayout>
                <Businesses />
              </AuthenticatedLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Agents />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/new"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <AgentEditor />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <AgentEditor />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <KnowledgeBase />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Workflows />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/new"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkflowBuilder />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkflowBuilder />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Conversations />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Settings />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AuthenticatedLayout>
                <Users />
              </AuthenticatedLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WhatsApp />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp/commands"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WhatsAppCommands />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp/docs"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WhatsAppDocs />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/saas-admin"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AuthenticatedLayout>
                <SaaSAdmin />
              </AuthenticatedLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      {/* Embed route for GHL iframe - no auth required, handles its own */}
            <Route
        path="/agency"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <AgencyPanel />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/embed" element={<Embed />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Build banner: print version info on app load to confirm fresh deploy vs cached.
    const APP_VERSION = process.env.REACT_APP_VERSION || "dev";
    const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || new Date().toISOString();
    console.log(
      `%c[LeadBot] APP_VERSION=${APP_VERSION} BUILD_TIME=${BUILD_TIME} API_BASE=${BACKEND_URL}`,
      "color:#1e90ff;font-weight:bold"
    );
    // Fetch backend version and log it next to the frontend build tag
    api.get("/version")
      .then((res) => {
        const v = res.data || {};
        console.log(
          `%c[LeadBot] BACKEND git_commit=${v.git_commit} neonize=${v.neonize_version} env=${v.environment} build_time=${v.build_time} deployment_id=${v.deployment_id}`,
          "color:#10b981;font-weight:bold"
        );
      })
      .catch((e) => {
        console.warn("[LeadBot] /api/version unavailable:", e?.response?.status || e?.message);
      });
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster theme="light" richColors position="bottom-right" closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
